import {
  collection,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentSnapshot,
  type Firestore,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { AuditLog } from '@kawai/shared';
import { db as defaultDb } from '../firebase.js';

/**
 * S-08: audit_logs 横断ビュー (saas-admin only) の filter 条件。
 *
 * - 全 filter は **AND 結合**。値未指定 (undefined) のフィールドは where 句に含めない
 * - 期間は ISO8601 (JST) 文字列で `createdAt >= startAt && createdAt <= endAt`
 * - 必要な composite index は `firestore.indexes.json` に追加 (S-08 Step 4)
 */
export interface AuditLogsFilters {
  tenantId?: string;
  action?: string;
  actorUid?: string;
  /** ISO8601 (JST) 文字列。`createdAt >= startAt` */
  startAt?: string;
  /** ISO8601 (JST) 文字列。`createdAt <= endAt` */
  endAt?: string;
}

export interface AuditLogsPage {
  items: AuditLog[];
  /** 次ページ取得用の cursor。null なら最終ページ */
  nextCursor: QueryDocumentSnapshot | null;
}

export const AUDIT_LOGS_PAGE_SIZES = [10, 25, 50, 100] as const;
export type AuditLogsPageSize = (typeof AUDIT_LOGS_PAGE_SIZES)[number];
export const DEFAULT_AUDIT_LOGS_PAGE_SIZE: AuditLogsPageSize = 25;

interface FetchAuditLogsPageOptions {
  filters?: AuditLogsFilters;
  pageSize?: AuditLogsPageSize;
  /** 前ページの `nextCursor` をそのまま渡す。初回ページは undefined */
  cursor?: QueryDocumentSnapshot | null;
  /** テスト時の Firestore モック注入 */
  db?: Firestore;
}

/**
 * S-08: audit_logs を filter + cursor pagination で取得する (saas-admin only)。
 *
 * - sort: `createdAt` desc 固定
 * - filter: tenantId / action / actorUid / 期間 の AND 結合
 * - cursor: `startAfter(prevLastDoc)` で次ページ。`hasMore` は items.length === pageSize で推測
 * - 戻り値の `nextCursor` は最終ページなら null
 */
export async function fetchAuditLogsPage({
  filters = {},
  pageSize = DEFAULT_AUDIT_LOGS_PAGE_SIZE,
  cursor,
  db = defaultDb,
}: FetchAuditLogsPageOptions = {}): Promise<AuditLogsPage> {
  const constraints: QueryConstraint[] = [];

  // where 句は値が指定されたフィールドのみ追加 (undefined はスキップ)。
  if (filters.tenantId !== undefined && filters.tenantId !== '') {
    constraints.push(where('tenantId', '==', filters.tenantId));
  }
  if (filters.action !== undefined && filters.action !== '') {
    constraints.push(where('action', '==', filters.action));
  }
  if (filters.actorUid !== undefined && filters.actorUid !== '') {
    constraints.push(where('actorUid', '==', filters.actorUid));
  }
  if (filters.startAt !== undefined && filters.startAt !== '') {
    constraints.push(where('createdAt', '>=', filters.startAt));
  }
  if (filters.endAt !== undefined && filters.endAt !== '') {
    constraints.push(where('createdAt', '<=', filters.endAt));
  }

  // sort + cursor + limit。cursor は startAfter で渡す
  constraints.push(orderBy('createdAt', 'desc'));
  if (cursor) {
    constraints.push(startAfter(cursor));
  }
  constraints.push(fbLimit(pageSize));

  const q: Query = query(collection(db, 'audit_logs'), ...constraints);
  const snap = await getDocs(q);

  const items: AuditLog[] = snap.docs.map((d) => {
    const data = d.data() as Omit<AuditLog, 'id'>;
    return { id: d.id, ...data };
  });

  // pageSize ぴったり取れた場合は次ページがある可能性ありとして cursor を返す。
  // 厳密には「次ページがあるかどうか」は次の fetch まで分からないが、UI 表示には十分。
  const lastDoc = snap.docs[snap.docs.length - 1] ?? null;
  const nextCursor: QueryDocumentSnapshot | null =
    items.length === pageSize ? (lastDoc as QueryDocumentSnapshot | null) : null;

  return { items, nextCursor };
}

/** Cursor 型を caller が知らずに opaque で扱うための re-export */
export type AuditLogsCursor = DocumentSnapshot;
